const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";

function toTokenId(value) {
  return value == null ? "" : String(value);
}

function toAddress(value) {
  return value ? String(value).toLowerCase() : null;
}

function getEventLogIndex(event) {
  const raw = event?.logIndex ?? event?.index ?? null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function getEventIdentity(event) {
  if (!event) return null;
  const txHash = event.transactionHash || "";
  const logIndex = getEventLogIndex(event);
  if (!txHash && logIndex == null) return null;
  return `${txHash}:${logIndex}`;
}

export function buildEventUpdate({ eventName, args, event }) {
  const safeArgs = args || {};

  switch (eventName) {
    case "Transfer": {
      const from = toAddress(safeArgs.from ?? safeArgs[0]);
      const to = safeArgs.to ?? safeArgs[1] ?? null;
      const tokenId = toTokenId(safeArgs.tokenId ?? safeArgs[2]);

      if (!tokenId) return null;

      if (from === ZERO_ADDRESS) {
        return {
          tokenId,
          patch: {
            tokenId,
            owner: to,
            isListed: false,
            isAuction: false,
            seller: null,
          },
          hydration: "full",
          reason: "mint",
        };
      }

      return {
        tokenId,
        patch: {
          tokenId,
          owner: to,
          seller: null,
        },
        hydration: "dynamic",
        reason: "transfer",
      };
    }

    case "CarbonCreditRetired": {
      const tokenId = toTokenId(safeArgs.tokenId ?? safeArgs[0]);
      const retiredBy = safeArgs.retiredBy ?? safeArgs.verifier ?? safeArgs[1] ?? null;
      if (!tokenId) return null;

      return {
        tokenId,
        patch: {
          tokenId,
          retired: true,
          retiredBy,
        },
        hydration: "dynamic",
        reason: "retired",
      };
    }

    case "TokenListed": {
      const tokenId = toTokenId(safeArgs.tokenId ?? safeArgs[0]);
      const seller = safeArgs.seller ?? safeArgs[1] ?? null;
      const price = safeArgs.price ?? safeArgs[2] ?? null;
      if (!tokenId) return null;
      return {
        tokenId,
        patch: {
          tokenId,
          isListed: true,
          isAuction: false,
          seller,
          price,
          auctionData: {
            active: false,
          },
        },
        hydration: "dynamic",
        reason: "listed",
      };
    }

    case "ListingCancelled": {
      const tokenId = toTokenId(safeArgs.tokenId ?? safeArgs[0]);
      if (!tokenId) return null;
      return {
        tokenId,
        patch: {
          tokenId,
          isListed: false,
          isAuction: false,
          price: null,
          seller: null,
          auctionData: {
            active: false,
          },
        },
        hydration: "dynamic",
        reason: "listing-cancelled",
      };
    }

    case "TokenPurchased": {
      const tokenId = toTokenId(safeArgs.tokenId ?? safeArgs[0]);
      const buyer = safeArgs.buyer ?? safeArgs[1] ?? null;
      if (!tokenId) return null;
      return {
        tokenId,
        patch: {
          tokenId,
          owner: buyer,
          isListed: false,
          isAuction: false,
          price: null,
          seller: null,
          auctionData: {
            active: false,
          },
        },
        hydration: "dynamic",
        reason: "purchased",
      };
    }

    case "AuctionStarted": {
      const tokenId = toTokenId(safeArgs.tokenId ?? safeArgs[0]);
      const seller = safeArgs.seller ?? safeArgs[1] ?? null;
      const minPrice = safeArgs.minPrice ?? safeArgs.startingPrice ?? safeArgs[2] ?? null;
      const endTime = safeArgs.endTime ?? safeArgs[3] ?? null;
      const auctionStartBlock = Number(event?.blockNumber ?? safeArgs.auctionStartBlock ?? 0) || null;
      if (!tokenId) return null;

      return {
        tokenId,
        patch: {
          tokenId,
          isListed: true,
          isAuction: true,
          seller,
          price: minPrice,
          auctionData: {
            active: true,
            seller,
            minPrice,
            startingPrice: minPrice,
            highestBid: null,
            highestBidder: null,
            endTime,
            auctionStartBlock,
          },
        },
        hydration: "dynamic",
        reason: "auction-started",
      };
    }

    case "NewHighestBid": {
      const tokenId = toTokenId(safeArgs.tokenId ?? safeArgs[0]);
      const bidder = safeArgs.bidder ?? safeArgs[1] ?? null;
      const amount = safeArgs.amount ?? safeArgs[2] ?? null;
      if (!tokenId) return null;

      return {
        tokenId,
        patch: {
          tokenId,
          isListed: true,
          isAuction: true,
          price: amount,
          auctionData: {
            active: true,
            highestBid: amount,
            highestBidder: bidder,
          },
        },
        hydration: "dynamic",
        reason: "new-highest-bid",
      };
    }

    case "AuctionEnded": {
      const tokenId = toTokenId(safeArgs.tokenId ?? safeArgs[0]);
      const winner = safeArgs.winner ?? safeArgs[1] ?? null;
      if (!tokenId) return null;

      return {
        tokenId,
        patch: {
          tokenId,
          owner: winner,
          isListed: false,
          isAuction: false,
          price: null,
          seller: null,
          auctionData: {
            active: false,
            auctionStartBlock: null,
          },
        },
        hydration: "dynamic",
        reason: "auction-ended",
      };
    }

    default:
      return null;
  }
}

export const MARKETPLACE_EVENT_NAMES = [
  "TokenListed",
  "ListingCancelled",
  "TokenPurchased",
  "AuctionStarted",
  "NewHighestBid",
  "AuctionEnded",
];

export const NFT_EVENT_NAMES = [
  "Transfer",
  "CarbonCreditRetired",
];
